import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import type { TripSegmentInput } from '@backoffice-frontend/graphql'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import { PlusIcon, TrashIcon } from '@moia-dev/pace-icons'
import { Button, Dialog, IconButton, Input, Text } from '@moia-dev/pace-web'
import { useCallback, useState } from 'react'
import { useRouteBuilderUpdateSavedTripMutation } from '../TripEditor/TripEditor.hook'
import { buildUpdateSavedTripVariables } from '../utils'
import type { SavedTrip } from './SavedTripCard'

type AssignedVtr = { vtr: string; codebeamerIds: string[] }

type VtrAssociationDialogProps = {
  id: string
  trip: SavedTrip
  segments: TripSegmentInput[]
  /** Lives on the trip details query, not on the `trip` list entry. */
  allWaypointsMode?: boolean
  onClose: () => void
  onSuccess: () => void
}

export const VtrAssociationDialog = ({
  id,
  trip,
  segments,
  allWaypointsMode = false,
  onClose,
  onSuccess,
}: VtrAssociationDialogProps) => {
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()

  const [vtrs, setVtrs] = useState<AssignedVtr[]>(
    trip.assignedVtrs?.map(v => ({
      vtr: v.vtr,
      codebeamerIds: v.codebeamerIds,
    })) ?? [],
  )
  const [newVtr, setNewVtr] = useState('')
  const [newCbIds, setNewCbIds] = useState('')

  const [updateVtrs, { loading }] = useErrorHandlingMutation(
    useRouteBuilderUpdateSavedTripMutation,
    {
      onCompleted: () => {
        enqueueMoiaSuccessNotification('VTR associations updated')
        onSuccess()
      },
    },
  )

  const handleAdd = useCallback(() => {
    const vtr = newVtr.trim()
    if (!vtr) return
    const codebeamerIds = newCbIds
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
    setVtrs(prev => [...prev, { vtr, codebeamerIds }])
    setNewVtr('')
    setNewCbIds('')
  }, [newVtr, newCbIds])

  const handleRemove = useCallback((index: number) => {
    setVtrs(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleSave = () => {
    updateVtrs({
      variables: buildUpdateSavedTripVariables({
        id: trip.id,
        name: trip.name,
        description: trip.description,
        tripType: trip.tripType,
        segments,
        assignedVtrs: vtrs,
        allWaypointsMode,
      }),
    })
  }

  return (
    <Dialog
      id={id}
      title="Manage VTR associations"
      onClose={onClose}
      actions={
        <>
          <Button label="Cancel" variant="secondary" onClick={onClose} />
          <Button
            label={loading ? 'Saving…' : 'Save'}
            variant="primary"
            onClick={handleSave}
            disabled={loading}
          />
        </>
      }
      css={{ maxWidth: 480 }}
    >
      <div
        css={theme => ({
          padding: theme.space.Space4,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space3,
        })}
      >
        {/* Current VTRs */}
        {vtrs.length === 0 ? (
          <Text size="sm" color="reduced">
            No VTR associations yet.
          </Text>
        ) : (
          <div css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {vtrs.map((v, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: pre-existing, tracked for follow-up cleanup
                key={`${v.vtr}-${i}`}
                css={theme => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.space.Space2,
                  padding: theme.space.Space2,
                  borderRadius: 6,
                  border: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
                })}
              >
                <div css={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" weight="bold">
                    {v.vtr}
                  </Text>
                  {v.codebeamerIds.length > 0 && (
                    <Text size="sm" color="reduced" css={{ fontSize: 11 }}>
                      CB: {v.codebeamerIds.join(', ')}
                    </Text>
                  )}
                </div>
                <IconButton
                  aria-label={`Remove ${v.vtr}`}
                  size="compact"
                  variant="plain"
                  onClick={() => handleRemove(i)}
                  icon={<TrashIcon />}
                />
              </div>
            ))}
          </div>
        )}

        {/* Add new VTR */}
        <div
          css={theme => ({
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space2,
            borderTop: `1px solid ${theme.semantic.ColorAccessoryReduced}`,
            paddingTop: theme.space.Space2,
          })}
        >
          <Text size="sm" color="reduced">
            Add VTR
          </Text>
          <Input
            size="compact"
            placeholder="VTR ticket key (e.g. VTR-123)"
            value={newVtr}
            onChange={e => setNewVtr(e.target.value)}
          />
          <Input
            size="compact"
            placeholder="Codebeamer IDs (comma-separated, optional)"
            value={newCbIds}
            onChange={e => setNewCbIds(e.target.value)}
          />
          <Button
            label="Add"
            variant="secondary"
            size="compact"
            icon={<PlusIcon />}
            onClick={handleAdd}
            disabled={!newVtr.trim()}
            css={{ alignSelf: 'flex-start' }}
          />
        </div>
      </div>
    </Dialog>
  )
}
